import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import {
  fetchNotificationHistoryRequest,
  fetchNotificationsRequest,
  retryNotificationRequest,
} from '../services/notificationService.js'
import {
  canRetryNotification,
  normalizeNotificationPayload,
  parseNotificationError,
} from '../utils/notificationUi.js'

export const useNotificationStore = defineStore('notification', () => {
  const notifications = ref([])
  const history = ref([])
  const loading = ref(false)
  const operationLoading = ref(false)
  const error = ref('')
  const degradedWarning = ref('')
  const lastSuccessfulFetchAt = ref('')
  const operationReference = ref('')
  const retryLocks = ref({})
  let requestToken = 0

  const notificationMap = computed(() => new Map(notifications.value.map((item) => [String(item.id), item])))

  const normalizeList = (payload) => {
    const list = Array.isArray(payload)
      ? payload
      : Array.isArray(payload?.items)
        ? payload.items
        : Array.isArray(payload?.notifications)
          ? payload.notifications
          : Array.isArray(payload?.results)
            ? payload.results
            : []
    return list.map((entry, index) => normalizeNotificationPayload(entry, index))
  }

  const setRetryLocked = (notificationId, locked) => {
    retryLocks.value = {
      ...retryLocks.value,
      [String(notificationId)]: Boolean(locked),
    }
  }

  const isRetryLocked = (notificationId) => Boolean(retryLocks.value[String(notificationId)])

  const fetchNotifications = async (params = {}, options = {}) => {
    const token = ++requestToken
    loading.value = true
    error.value = ''
    degradedWarning.value = ''

    try {
      const data = await fetchNotificationsRequest(params, options)
      if (token !== requestToken) return { success: false, cancelled: true }
      notifications.value = normalizeList(data)
      if (data?.partial === true || data?.degraded === true) {
        degradedWarning.value = 'Notification status feed may be incomplete while delivery sync is running.'
      }
      lastSuccessfulFetchAt.value = new Date().toISOString()
      return { success: true, data: notifications.value }
    } catch (err) {
      if (token !== requestToken) return { success: false, cancelled: true }
      error.value = parseNotificationError(err, 'Could not load notifications.')
      return { success: false, error: error.value }
    } finally {
      if (token === requestToken) {
        loading.value = false
      }
    }
  }

  const fetchHistory = async (params = {}, options = {}) => {
    const token = ++requestToken
    loading.value = true
    error.value = ''

    try {
      const data = await fetchNotificationHistoryRequest(params, options)
      if (token !== requestToken) return { success: false, cancelled: true }
      history.value = normalizeList(data)
      lastSuccessfulFetchAt.value = new Date().toISOString()
      return { success: true, data: history.value }
    } catch (err) {
      if (token !== requestToken) return { success: false, cancelled: true }
      error.value = parseNotificationError(err, 'Could not load notification history.')
      return { success: false, error: error.value }
    } finally {
      if (token === requestToken) {
        loading.value = false
      }
    }
  }

  const retryNotification = async (notificationId, payload = {}) => {
    if (isRetryLocked(notificationId)) {
      return { success: false, code: 'NOTIFICATION_LOCKED', error: 'A retry is already in progress for this notification.' }
    }

    const notification = notificationMap.value.get(String(notificationId))
    if (notification && !canRetryNotification(notification)) {
      return { success: false, code: 'NOTIFICATION_RETRY_BLOCKED', error: 'This notification cannot be retried in its current state.' }
    }

    operationLoading.value = true
    error.value = ''
    operationReference.value = ''
    setRetryLocked(notificationId, true)
    try {
      const data = await retryNotificationRequest(notificationId, payload)
      operationReference.value = String(data?.correlation_ref || data?.reference || '')
      await fetchNotifications(payload)
      await fetchHistory(payload)
      return { success: true, data: normalizeNotificationPayload(data) }
    } catch (err) {
      error.value = parseNotificationError(err, 'Could not retry notification delivery.')
      operationReference.value = String(err?.response?.data?.correlation_ref || err?.response?.headers?.['x-correlation-id'] || '')
      return { success: false, error: error.value }
    } finally {
      setRetryLocked(notificationId, false)
      operationLoading.value = false
    }
  }

  return {
    notifications,
    history,
    loading,
    operationLoading,
    error,
    degradedWarning,
    lastSuccessfulFetchAt,
    operationReference,
    retryLocks,
    fetchNotifications,
    fetchHistory,
    retryNotification,
    isRetryLocked,
  }
})

